"use client";

import { useCallback, useState } from "react";
import { objectUrlFromRenderUrl } from "@/lib/storage/public-url";

/**
 * Survive a dead image-transform layer: when a `…/render/image/public/…` URL
 * fails to load, retry ONCE against the object endpoint underneath it.
 *
 * WHY: `imagePresetUrl` sends almost every stored image through Supabase's
 * transformer, and that transformer can be absent or failing while the bytes
 * themselves are fine — it is a Pro-plan service our self-hosted stacks do not
 * run, and on prod a failed transform answers with HTTP 400 + JSON rather than
 * an image. Before this hook the codebase had no fallback anywhere: a transform
 * outage meant broken images on every surface at once, with the originals
 * sitting one URL away (handoff continuation §3.2).
 *
 * WHAT IT IS NOT: a size optimisation. The twin is the ORIGINAL, full-size
 * object — the trade is bytes for a visible picture, which is right for a
 * failure and wrong for a default. Nothing should call this on the happy path.
 *
 * ONE downgrade, never a loop. If the object URL fails too, `report()` returns
 * `false` and the caller does whatever it did before this hook existed (grey
 * box, initials, broken icon). The alternative — re-deriving the twin from the
 * twin — is how a fallback becomes an infinite request storm.
 *
 * The `src`-changed reset is done DURING RENDER, not in an effect: an effect
 * would paint one frame of the previous image's fallback URL under the new
 * `src`, and `useEffect` for deriving state from props is banned by the repo's
 * frontend gate. This is the react.dev "adjusting state when a prop changes"
 * pattern.
 *
 * @see lib/storage/url.ts::objectUrlFromRenderUrl — the pure half
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_CONTINUATION_2026_07_28.md §3.2
 */
export function useImageTransformFallback(preferredSrc: string): {
  /** What to render. The preferred URL until it fails, then the object twin. */
  src: string;
  /**
   * Call from the consumer's error channel — `<img onError>`, Radix's
   * `onLoadingStatusChange === "error"`, whatever the primitive exposes.
   *
   * Returns `true` when a retry was armed (the caller should stay quiet and let
   * the new `src` load) and `false` when there is nothing left to try (the
   * caller should show its own failure state).
   */
  report: () => boolean;
} {
  const [seenSrc, setSeenSrc] = useState(preferredSrc);
  const [downgraded, setDowngraded] = useState(false);

  if (preferredSrc !== seenSrc) {
    setSeenSrc(preferredSrc);
    setDowngraded(false);
  }

  // Recomputed rather than stored: derived state that is cheap to derive should
  // not be mirrored, and storing it would give us two sources of truth for
  // "which URL is on screen".
  const twin = objectUrlFromRenderUrl(preferredSrc);
  const src = downgraded && twin ? twin : preferredSrc;

  // Plain closure over the render's values, NOT a state updater that also
  // computes the return value: React invokes updaters twice under StrictMode,
  // so anything an updater writes to an outer variable is a side effect in a
  // place that is required to be pure. The identity of `report` changes when
  // the fallback becomes available or spent, which is correct — it is a
  // different decision at that point.
  const report = useCallback(() => {
    if (downgraded || twin === null) return false;
    setDowngraded(true);
    return true;
  }, [downgraded, twin]);

  return { src, report };
}
